import { useSignal } from "@preact/signals";
import { rpc } from "../../rpc";
import { currentUser } from "../../stores/current-user";
import { showError } from "../../stores/errors";
import { IcoCheck } from "../icons";
import { Button } from "./button";
import { SlideOver } from "./slide-over";
import { TextArea } from "./textarea";

type FeedbackCategory = "bug" | "design" | "ux" | "content";

const categories: { value: FeedbackCategory; label: string; icon: string }[] = [
  { value: "bug", label: "Bug", icon: "🐛" },
  { value: "design", label: "Design", icon: "🎨" },
  { value: "ux", label: "UX", icon: "✨" },
  { value: "content", label: "Content", icon: "📝" },
];

export function FeedbackWidget() {
  const isOpen = useSignal(false);
  const category = useSignal<FeedbackCategory | null>(null);
  const comment = useSignal("");
  const submitting = useSignal(false);
  const submitted = useSignal(false);

  // Only show for authenticated users
  if (!currentUser.value) return null;

  function reset() {
    category.value = null;
    comment.value = "";
    submitting.value = false;
    submitted.value = false;
  }

  function handleClose() {
    isOpen.value = false;
    // Reset after animation
    setTimeout(reset, 300);
  }

  async function handleSubmit() {
    if (!category.value || !comment.value.trim()) return;

    submitting.value = true;
    try {
      await rpc.feedback.submit({
        category: category.value,
        comment: comment.value.trim(),
        pageUrl: window.location.pathname + window.location.search,
      });
      submitted.value = true;
      setTimeout(handleClose, 2000);
    } catch (err) {
      showError(
        "Failed to send feedback",
        err instanceof Error ? err.message : "Please try again.",
      );
    } finally {
      submitting.value = false;
    }
  }

  const canSubmit = category.value !== null && comment.value.trim().length > 0;

  return (
    <>
      {/* Floating action button */}
      <button
        type="button"
        onClick={() => {
          isOpen.value = true;
        }}
        class="fixed bottom-6 right-6 z-[60] size-12 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 active:bg-primary-800 transition-colors flex items-center justify-center"
        aria-label="Send feedback"
      >
        <svg
          class="size-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </button>

      {/* Feedback form slide-over */}
      <SlideOver
        open={isOpen.value}
        onClose={handleClose}
        title="Send Feedback"
        footer={
          submitted.value ? undefined : (
            <div class="flex gap-3 justify-end">
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <div data-brand="blue">
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  loading={submitting.value}
                  disabled={!canSubmit}
                >
                  Submit
                </Button>
              </div>
            </div>
          )
        }
      >
        {submitted.value ? (
          <div class="flex flex-col items-center justify-center py-12 text-center">
            <div class="size-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <IcoCheck class="size-6 text-green-600" />
            </div>
            <p class="text-lg font-medium text-text">
              Thanks for your feedback!
            </p>
            <p class="text-sm text-text-muted mt-1">We'll review it shortly.</p>
          </div>
        ) : (
          <div class="space-y-6">
            {/* Page context */}
            <div>
              <span class="block text-sm font-medium text-text-secondary mb-1">
                Page
              </span>
              <p class="text-sm text-text-muted bg-surface-muted rounded-lg px-3 py-2">
                {window.location.pathname}
              </p>
            </div>

            {/* Category selection */}
            <div>
              <span class="block text-sm font-medium text-text-secondary mb-2">
                Category
              </span>
              <div class="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => {
                      category.value = cat.value;
                    }}
                    class={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      category.value === cat.value
                        ? "border-primary-600 bg-primary-50 text-primary-700"
                        : "border-border text-text-secondary hover:bg-surface-hover"
                    }`}
                  >
                    <span>{cat.icon}</span>
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Comment */}
            <div>
              <span class="block text-sm font-medium text-text-secondary mb-1">
                Comment
              </span>
              <TextArea
                value={comment.value}
                onInput={(e) => {
                  comment.value = (e.target as HTMLTextAreaElement).value;
                }}
                placeholder="Describe your feedback..."
                class="w-full min-h-[100px] rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autofocus
              />
            </div>

            {/* Submitting as */}
            <p class="text-xs text-text-muted">
              Submitting as{" "}
              <span class="font-medium">{currentUser.value?.name}</span>
            </p>
          </div>
        )}
      </SlideOver>
    </>
  );
}
